import {
    TYPESENSE_ORGANIZATIONS_COLLECTION_NAME,
    TYPESENSE_PARTS_COLLECTION_NAME,
    TYPESENSE_PROJECTS_COLLECTION_NAME,
    TYPESENSE_USERS_COLLECTION_NAME,
} from "@buildwithflux/constants";
import {
    SitemapContent,
    typesenseConfigForPartCollection,
    typesenseConfigForSiteMapUsers,
    typesenseConfigForSiteSearchOrganizations,
    typesenseOrganizationsCollectionDefaultSort,
    typesenseProjectsCollectionDefaultSort,
    typesenseTypoConfigForProjectsCollection,
    typesenseUsersCollectionDefaultSort,
} from "@buildwithflux/search";
import {type DocumentDataForUrlRouting, getDocumentUrl, Routes} from "@buildwithflux/url";
import StarBorderIcon from "@mui/icons-material/StarBorder";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import Box from "@mui/material/Box";
import Breadcrumbs from "@mui/material/Breadcrumbs";
import CircularProgress from "@mui/material/CircularProgress";
import Container from "@mui/material/Container";
import FormControl from "@mui/material/FormControl";
import Grid from "@mui/material/Grid";
import InputLabel from "@mui/material/InputLabel";
import Link from "@mui/material/Link";
import List from "@mui/material/List";
import MenuItem from "@mui/material/MenuItem";
import Paper from "@mui/material/Paper";
import Select from "@mui/material/Select";
import Typography from "@mui/material/Typography";
import { useCallback, useEffect, useState } from "react";
import {
    useParams,
    useSearchParams,
    useNavigate,
    useLoaderData,
    type LoaderFunctionArgs,
    type MetaArgs,
    type MetaDescriptor,
} from "react-router";
import type { Client } from "typesense";

import { useAccountsViewClient } from "../hooks/useAccountsViewClient";
import { useDocumentTitle } from "../hooks/useDocumentTitle";
import { useTypesenseClient } from "../hooks/useTypesenseClient";
import { APP_DESCRIPTION, siteMetaDescriptors, toCanonicalUrl } from "../seo";
import type { SiteSearchLoadContext } from "../server/loaderContext";
import { typesenseSearch } from "../server/typesenseRest";
import { getPreviewImageUrl } from "../utils/imageUrl";

interface SearchResult {
    uid: string;
    name: string;
    description?: string;
    url: string;
    thumbUrl?: string;
    usage_count?: number;
    star_count?: number;
}

function formatCount(count: number | undefined): string {
    if (!count) return "0";
    if (count >= 1000) return `${(count / 1000).toFixed(1)}k`;
    return String(count);
}

function ResultCard({ result }: { result: SearchResult }) {
    return (
        <Paper
            square
            component="li"
            sx={{ mt: 1, p: 2, minHeight: "113px" }}
        >
            <Box sx={{ display: "flex", justifyContent: "flex-start" }}>
                <Box sx={{ mr: 1, flexShrink: 0 }}>
                    <Link href={result.url}>
                        <Box
                            component="img"
                            src={result.thumbUrl ?? ""}
                            alt={result.name}
                            draggable={false}
                            sx={{
                                width: 124,
                                height: 124,
                                objectFit: "contain",
                                display: result.thumbUrl ? "block" : "none",
                            }}
                        />
                    </Link>
                </Box>

                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Link href={result.url} color="text.primary" underline="none">
                        <Typography color="text.primary" variant="h6" component="h2" sx={{ mb: 0.5 }}>
                            {result.name}
                        </Typography>
                    </Link>

                    {result.description && (
                        <Link href={result.url} color="text.primary" underline="none" sx={{ display: "block" }}>
                            <Typography color="text.primary" variant="body2">
                                {result.description}
                            </Typography>
                        </Link>
                    )}

                    <Box sx={{ mt: 1 }}>
                        <Typography
                            color="text.secondary"
                            variant="body2"
                            component="span"
                            sx={{ mr: 2, display: "inline-flex", alignItems: "center", gap: 0.5 }}
                        >
                            <TrendingUpIcon sx={{ fontSize: 16 }} />
                            {formatCount(result.usage_count)} Uses
                        </Typography>
                        <Typography
                            color="text.secondary"
                            variant="body2"
                            component="span"
                            sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}
                        >
                            <StarBorderIcon sx={{ fontSize: 16 }} />
                            {formatCount(result.star_count)} Stars
                        </Typography>
                    </Box>
                </Box>
            </Box>
        </Paper>
    );
}

/**
 * The users listing reads the accounts view, not the legacy `users` collection:
 * only the accounts view enforces `isAccountEligible` (no anonymous, no test
 * accounts), so the legacy one publishes crawlable links to profiles that 404.
 */
function usesAccountsView(searchIndex: string | undefined): boolean {
    return searchIndex === "users";
}

/** Mirrors `SitemapContent.accounts.users.searchOptions.numericFilters` (isSignificantUser). */
const ACCOUNTS_VIEW_USERS_FILTER_BY = "accountType:=user && (follower_count:>0 || parts_count:>0)";

const ACCOUNTS_VIEW_USERS_SORT_BY = "follower_count:desc,star_count:desc";

/** The public tier omits `full_name`, so the handle is the display fallback. */
function accountToSearchResult(doc: Record<string, unknown>): SearchResult | null {
    const handle = (doc.handle ?? "") as string;
    if (!handle) return null;

    return {
        uid: (doc.uid ?? doc.id ?? handle) as string,
        name: ((doc.full_name ?? "") as string) || handle,
        description: (doc.tag_line ?? "") as string,
        url: `/${handle}`,
        star_count: (doc.star_count ?? 0) as number,
        usage_count: (doc.documents_count ?? 0) as number,
    };
}

function getCollectionName(searchIndex: string | undefined): string {
    switch (searchIndex) {
        case "projects": return TYPESENSE_PROJECTS_COLLECTION_NAME;
        case "components": return TYPESENSE_PARTS_COLLECTION_NAME;
        case "users": return TYPESENSE_USERS_COLLECTION_NAME;
        case "orgs": return TYPESENSE_ORGANIZATIONS_COLLECTION_NAME;
        default: return "";
    }
}

function getSearchParams(collectionName: string) {
    if (collectionName === TYPESENSE_PROJECTS_COLLECTION_NAME) {
        return {
            query_by: "uid,name,description,slug",
            sort_by: typesenseProjectsCollectionDefaultSort,
            ...typesenseTypoConfigForProjectsCollection,
        };
    }
    if (collectionName === TYPESENSE_USERS_COLLECTION_NAME) {
        return {
            query_by: "full_name,handle",
            sort_by: typesenseUsersCollectionDefaultSort,
            ...typesenseConfigForSiteMapUsers,
        };
    }
    if (collectionName === TYPESENSE_ORGANIZATIONS_COLLECTION_NAME) {
        return {
            query_by: "displayName,handle",
            sort_by: typesenseOrganizationsCollectionDefaultSort,
            ...typesenseConfigForSiteSearchOrganizations,
        };
    }
    if (collectionName === TYPESENSE_PARTS_COLLECTION_NAME) {
        return {
            query_by: "uid,name,nameSuffixes,description",
            ...typesenseConfigForPartCollection,
        };
    }
    return {};
}

function getSortBy(searchIndex: string | undefined, sortParam: string): string {
    if (searchIndex === "users") {
        return "follower_count:desc,star_count:desc,_text_match:desc";
    }
    if (searchIndex === "orgs") {
        return "follower_count:desc,documentsCount:desc,_text_match:desc";
    }
    if (sortParam === "recent") {
        return "updated_at:desc,star_count:desc,_text_match:desc";
    }
    if (sortParam === "usage") {
        if (searchIndex === "projects") {
            return "usage_count:desc,star_count:desc,_text_match:desc";
        }
        if (searchIndex === "components") {
            return "contentScore:desc,star_count:desc,_text_match:desc";
        }
    }
    return "star_count:desc,updated_at:desc,_text_match:desc";
}

function resultToSearchResult(doc: Record<string, unknown>, searchIndex: string | undefined): SearchResult | null {
    const uid = (doc.uid ?? doc.objectID ?? "") as string;
    const name = (doc.name ?? doc.full_name ?? doc.displayName ?? "") as string;
    const description = (doc.description ?? "") as string;
    const handle = (doc.owner_handle ?? doc.handle ?? "") as string;
    const starCount = (doc.star_count ?? 0) as number;
    const usageCount = ((doc as Record<string, unknown>).use_count ?? (doc as Record<string, unknown>).usage_count ?? 0) as number;
    const previewImage = (doc.preview_image ?? "") as string;

    const thumbUrl = getPreviewImageUrl(previewImage || undefined, 124);

    const slug = (doc.slug ?? "") as string;
    const projectUrl = (doc.project_url ?? "") as string;

    let url: string;
    if (searchIndex === "users" || searchIndex === "orgs") {
        url = handle ? `/${handle}` : `/`;
    } else if (projectUrl) {
        // Canonical slug~short_id URL denormalized onto the search record by the backend
        url = projectUrl;
    } else if (uid && slug && handle) {
        // Projects collection carries slug/short_id, so build the canonical slug~short_id URL
        url = getDocumentUrl(doc as DocumentDataForUrlRouting);
    } else {
        // No canonical URL available (e.g. a part whose project_url has not been
        // backfilled). Omit the hit rather than emit a non-routable
        // /projects/<uid> link, which 404s (a part uid is not a project uid).
        return null;
    }

    return { uid, name, description, url, thumbUrl, star_count: starCount, usage_count: usageCount };
}

async function fetchAccountsPage(
    client: Client,
    collectionName: string,
    query: string,
    page: number,
): Promise<{ results: SearchResult[]; totalHits: number }> {
    const response = await client.collections(collectionName).documents().search({
        q: query || "*",
        query_by: "full_name,handle",
        filter_by: ACCOUNTS_VIEW_USERS_FILTER_BY,
        sort_by: ACCOUNTS_VIEW_USERS_SORT_BY,
        per_page: 25,
        page,
    });

    const results = (response.hits ?? [])
        .map((hit) => accountToSearchResult(hit.document as Record<string, unknown>))
        .filter((result): result is SearchResult => result !== null);

    return { results, totalHits: response.found };
}

async function fetchPage(
    client: Client,
    collectionName: string,
    query: string,
    page: number,
    sortBy: string,
): Promise<{ results: SearchResult[]; totalHits: number; searchIndex: string }> {
    const searchParams = getSearchParams(collectionName);
    const searchIndex = collectionName === TYPESENSE_PROJECTS_COLLECTION_NAME ? "projects"
        : collectionName === TYPESENSE_PARTS_COLLECTION_NAME ? "components"
        : collectionName === TYPESENSE_USERS_COLLECTION_NAME ? "users"
        : "orgs";

    const response = await client.collections(collectionName).documents().search({
        q: query || "*",
        per_page: 25,
        page,
        sort_by: sortBy,
        ...searchParams,
    });

    const results = (response.hits ?? [])
        .map((hit) => resultToSearchResult(hit.document as Record<string, unknown>, searchIndex))
        .filter((result): result is SearchResult => result !== null);

    return { results, totalHits: response.found, searchIndex };
}

const SORT_OPTIONS = [
    { value: "popularity", label: "Most Starred" },
    { value: "recent", label: "Most Recent" },
    { value: "usage", label: "Most Used" },
] as const;

export interface SubCategoryLoaderData {
    categoryKey: string;
    subcategoryKey: string;
    sortParam: string;
    page: number;
    results: SearchResult[];
    totalHits: number;
}

/**
 * Server loader: runs in the SSR worker so the subcategory listing renders into
 * the SSR HTML (the SEO surface) instead of being client-fetched after
 * hydration. Returns `null` when the worker provided no scoped key or the
 * subcategory is unknown, in which case the component falls back to the
 * client-side fetch.
 */
export async function loader({ request, params, context }: LoaderFunctionArgs): Promise<SubCategoryLoaderData | null> {
    const ctx = context as SiteSearchLoadContext;
    const typesenseApiKey = ctx.typesenseApiKey;
    if (!typesenseApiKey) return null;

    const categoryKey = (params.category ?? "").toLowerCase();
    const subcategoryKey = (params.subcategory ?? "").toLowerCase();
    const subcategoryData = SitemapContent[categoryKey]?.subcategories[subcategoryKey];
    if (!subcategoryData) return null;

    const collectionName = getCollectionName(subcategoryData.searchIndex);
    if (!collectionName) return null;

    const url = new URL(request.url);
    const sortParam = url.searchParams.get("sort") ?? "popularity";
    const page = parseInt(url.searchParams.get("page") ?? "1", 10) || 1;
    const sortBy = getSortBy(subcategoryData.searchIndex, sortParam);
    const query = subcategoryData.query ?? "*";

    if (usesAccountsView(subcategoryData.searchIndex)) {
        const accountsView = ctx.accountsView;
        if (!accountsView) return null;
        const accountsResponse = await typesenseSearch(accountsView.apiKey, accountsView.collection, {
            q: query || "*",
            query_by: "full_name,handle",
            filter_by: ACCOUNTS_VIEW_USERS_FILTER_BY,
            sort_by: ACCOUNTS_VIEW_USERS_SORT_BY,
            per_page: 25,
            page,
        });
        const accountResults = accountsResponse.hits
            .map((hit) => accountToSearchResult(hit.document))
            .filter((result): result is SearchResult => result !== null);
        return {
            categoryKey,
            subcategoryKey,
            sortParam,
            page,
            results: accountResults,
            totalHits: accountsResponse.found,
        };
    }
    const searchIndex =
        collectionName === TYPESENSE_PROJECTS_COLLECTION_NAME
            ? "projects"
            : collectionName === TYPESENSE_PARTS_COLLECTION_NAME
              ? "components"
              : collectionName === TYPESENSE_USERS_COLLECTION_NAME
                ? "users"
                : "orgs";

    const response = await typesenseSearch(typesenseApiKey, collectionName, {
        q: query || "*",
        per_page: 25,
        page,
        ...getSearchParams(collectionName),
        sort_by: sortBy,
    });

    const results = response.hits
        .map((hit) => resultToSearchResult(hit.document, searchIndex))
        .filter((result): result is SearchResult => result !== null);
    return { categoryKey, subcategoryKey, sortParam, page, results, totalHits: response.found };
}

function getSortSuffix(sortParam: string): string {
    return sortParam === "popularity" ? " - Most Popular"
        : sortParam === "recent" ? " - Recently Updated"
        : sortParam === "usage" ? " - Most Used"
        : "";
}

function getPageTitle(
    categoryData: (typeof SitemapContent)[string],
    subcategoryData: (typeof SitemapContent)[string]["subcategories"][string],
    sortParam: string,
): string {
    const sortSuffix = getSortSuffix(sortParam);
    return subcategoryData.pageMetaTitle
        ? `${subcategoryData.pageMetaTitle}${sortSuffix} on Flux`
        : `${categoryData.name} ${subcategoryData.name}${sortSuffix} on Flux`;
}

export function meta({ data, params, location }: MetaArgs): MetaDescriptor[] {
    const categoryKey = params.category?.toLowerCase() ?? "";
    const subcategoryKey = params.subcategory?.toLowerCase() ?? "";
    const categoryData = SitemapContent[categoryKey];
    const subcategoryData = categoryData?.subcategories[subcategoryKey];
    if (!categoryData || !subcategoryData) {
        return [{ title: "Page not found" }];
    }

    const searchParams = new URLSearchParams(location.search);
    const sortParam = searchParams.get("sort") ?? "popularity";
    const pageNum = parseInt(searchParams.get("page") ?? "1", 10) || 1;

    const absoluteUrl = toCanonicalUrl(`/sitemap/${categoryKey}/${subcategoryKey}`);
    const metaTitle = `Flux Sitemap/${categoryData.name}/${subcategoryData.name}`;

    const descriptors: MetaDescriptor[] = [
        { title: getPageTitle(categoryData, subcategoryData, sortParam) },
        { name: "description", content: subcategoryData.description ?? APP_DESCRIPTION },
        { tagName: "link", rel: "canonical", href: absoluteUrl },
        { tagName: "link", rel: "alternate", type: "text/markdown", href: `${absoluteUrl}.md` },
        { property: "og:title", content: metaTitle },
        { property: "og:description", content: APP_DESCRIPTION },
        { property: "og:type", content: "website" },
        { property: "og:url", content: absoluteUrl },
        { name: "twitter:title", content: metaTitle },
        { name: "twitter:description", content: APP_DESCRIPTION },
        ...siteMetaDescriptors(),
    ];

    const loaderData = data as SubCategoryLoaderData | null | undefined;
    const totalHits = loaderData?.totalHits ?? 0;
    if (pageNum < Math.ceil(totalHits / 25)) {
        descriptors.push({
            tagName: "link",
            rel: "next",
            href: `${absoluteUrl}?sort=${sortParam}&page=${pageNum + 1}`,
        });
    }

    descriptors.push({
        "script:ld+json": {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
                { "@type": "ListItem", position: 1, name: "Home", item: "https://flux.ai" },
                { "@type": "ListItem", position: 2, name: "Sitemap", item: toCanonicalUrl("/sitemap") },
                { "@type": "ListItem", position: 3, name: categoryData.name, item: toCanonicalUrl(`/sitemap/${categoryKey}`) },
                { "@type": "ListItem", position: 4, name: subcategoryData.name, item: absoluteUrl },
            ],
        },
    });

    if (subcategoryData.description) {
        descriptors.push({
            "script:ld+json": {
                "@context": "https://schema.org",
                "@type": "CollectionPage",
                name: subcategoryData.pageMetaTitle ?? `${categoryData.name} ${subcategoryData.name}`,
                description: subcategoryData.description,
                url: absoluteUrl,
            },
        });
    }

    return descriptors;
}

export default function SubCategoryPage() {
    const { category, subcategory } = useParams<{ category: string; subcategory: string }>();
    const [urlSearchParams] = useSearchParams();
    const navigate = useNavigate();

    const categoryKey = category?.toLowerCase() ?? "";
    const subcategoryKey = subcategory?.toLowerCase() ?? "";
    const categoryData = SitemapContent[categoryKey];
    const subcategoryData = categoryData?.subcategories[subcategoryKey];

    const searchIndex = subcategoryData?.searchIndex;
    const accountsIndex = usesAccountsView(searchIndex);
    const collectionName = getCollectionName(searchIndex);

    const query = subcategoryData?.query ?? "*";
    const sortParam = urlSearchParams.get("sort") ?? "popularity";
    const sortBy = getSortBy(searchIndex, sortParam);

    const pageNum = parseInt(urlSearchParams.get("page") ?? "1", 10);

    // Only the collection this listing reads from needs a scoped key.
    const client = useTypesenseClient(!accountsIndex);
    const accountsView = useAccountsViewClient(null, accountsIndex);

    // SSR loader already fetched this exact listing; render it directly and skip
    // the client fetch. On the R2 SPA-shell path there is no loader, so we fall
    // back to fetching client-side.
    const loaderData = useLoaderData() as SubCategoryLoaderData | null | undefined;
    const ssr =
        loaderData &&
        loaderData.categoryKey === categoryKey &&
        loaderData.subcategoryKey === subcategoryKey &&
        loaderData.sortParam === sortParam &&
        loaderData.page === pageNum
            ? loaderData
            : null;

    const [clientResults, setClientResults] = useState<SearchResult[]>([]);
    const [clientTotalHits, setClientTotalHits] = useState(0);
    const [loading, setLoading] = useState(!ssr);

    const results = ssr ? ssr.results : clientResults;
    const totalHits = ssr ? ssr.totalHits : clientTotalHits;

    const handleSortChange = useCallback(
        (event: { target: { value: string } }) => {
            const newSort = event.target.value;
            void navigate(`${Routes.SITEMAP}/${categoryKey}/${subcategoryKey}?sort=${newSort}&page=1`);
        },
        [navigate, categoryKey, subcategoryKey],
    );

    const loadResults = useCallback(async () => {
        if (ssr || !collectionName) return;
        const fetcher = accountsIndex
            ? accountsView &&
              (() => fetchAccountsPage(accountsView.client, accountsView.collection, query, pageNum))
            : client && (() => fetchPage(client, collectionName, query, pageNum, sortBy));
        if (!fetcher) return;
        setLoading(true);
        try {
            const data = await fetcher();
            setClientResults(data.results);
            setClientTotalHits(data.totalHits);
        } catch (err) {
            // eslint-disable-next-line no-console
            console.error("Search failed:", err);
        } finally {
            setLoading(false);
        }
    }, [ssr, accountsIndex, client, accountsView, collectionName, query, pageNum, sortBy]);

    useEffect(() => {
        void loadResults();
    }, [loadResults]);

    useDocumentTitle(
        categoryData && subcategoryData
            ? getPageTitle(categoryData, subcategoryData, sortParam)
            : "Page not found",
    );

    if (!categoryData || !subcategoryData) {
        return (
            <Container maxWidth="md" sx={{ py: 4 }}>
                <Typography variant="h4" component="h1">Page not found</Typography>
            </Container>
        );
    }

    const totalPages = Math.ceil(totalHits / 25);

    return (
        <>
            <Container maxWidth="md">
                <Grid container>
                    <Grid size={12}>
                        <Breadcrumbs aria-label="Breadcrumb" sx={{ mb: 2 }}>
                            <Link color="inherit" href={Routes.SITEMAP}>
                                Sitemap
                            </Link>
                            <Link color="inherit" href={`${Routes.SITEMAP}/${categoryKey}`}>
                                {categoryData.name}
                            </Link>
                            <Typography color="text.primary">{subcategoryData.name}</Typography>
                        </Breadcrumbs>

                        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 2 }}>
                            <Typography
                                color="text.primary"
                                variant="h2"
                                component="h1"
                                sx={{ wordBreak: "break-word" }}
                            >
                                {subcategoryData.name}
                            </Typography>

                            <FormControl size="small" sx={{ minWidth: 150 }}>
                                <InputLabel>Sort by</InputLabel>
                                <Select
                                    value={sortParam}
                                    label="Sort by"
                                    onChange={handleSortChange}
                                >
                                    {SORT_OPTIONS.map((opt) => (
                                        <MenuItem key={opt.value} value={opt.value}>
                                            {opt.label}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Box>

                        {subcategoryData.description && (
                            <Typography color="text.secondary" variant="body1" sx={{ mb: 3 }}>
                                {subcategoryData.description}
                            </Typography>
                        )}

                        {loading ? (
                            <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
                                <CircularProgress />
                            </Box>
                        ) : (
                            <>
                                <List aria-label={subcategoryData.name} disablePadding>
                                    {results.map((result) => (
                                        <ResultCard key={result.uid} result={result} />
                                    ))}
                                </List>

                                {totalPages > 1 && (
                                    <Box sx={{ display: "flex", justifyContent: "center", gap: 2, py: 4 }}>
                                        {pageNum > 1 && (
                                            <Link href={`${Routes.SITEMAP}/${categoryKey}/${subcategoryKey}?page=${pageNum - 1}&sort=${sortParam}`}>
                                                Previous
                                            </Link>
                                        )}
                                        <Typography color="text.secondary" variant="body2">
                                            Page {pageNum} of {totalPages}
                                        </Typography>
                                        {pageNum < totalPages && (
                                            <Link href={`${Routes.SITEMAP}/${categoryKey}/${subcategoryKey}?page=${pageNum + 1}&sort=${sortParam}`}>
                                                Next
                                            </Link>
                                        )}
                                    </Box>
                                )}
                            </>
                        )}
                    </Grid>
                </Grid>
            </Container>
        </>
    );
}
